import React, { FC, useCallback, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  ModalBody,
  ModalFooter,
  Row,
} from 'reactstrap';
import { Raffle, RunRaffleResults } from '../../../../../shared/orders/raffle';
import {
  InlineHelp,
  Loading,
  MaterialIcon,
  RadioButtonGroup,
  RichMarkdownIt,
} from '../../../../components';
import { UserSelector } from '../../../../components/UserSelector';
import { JSONForm, PreSubmitData } from '../../../../components/json-form/JSONForm';
import { useTranslation } from '../../../../translations';
import { useBoolState } from '../../../../utils';
import { captureError } from '../../../../utils/errorHandling';

interface Props {
  readonly raffle: Raffle;
}

export const RunRaffleComponent: FC<Props> = ({ raffle }) => {
  const { ts } = useTranslation();
  const [isRunning, setRunning, setNotRunning] = useBoolState(false);
  const [results, setResults] = useState<RunRaffleResults>();
  const onSuccess = useCallback(
    (raffleResults: RunRaffleResults) => {
      setNotRunning();
      setResults(raffleResults);
    },
    [setNotRunning],
  );

  const onFailure = useCallback(
    (err: Error) => {
      setNotRunning();
      captureError(err);
      return true;
    },
    [setNotRunning],
  );

  const closeModal = useCallback(() => {
    setResults(undefined);
  }, []);

  return (
    <Card>
      <CardBody>
        {isRunning && (
          <Loading>
            <p>{ts('running_raffle_this_may_take_a_few_minutes')}</p>
          </Loading>
        )}
        {results ? (
          <RaffleResultsViewer onClick={closeModal} results={results} />
        ) : (
          <DrawRaffleModalForm
            onFailure={onFailure}
            onSubmit={setRunning}
            onSuccess={onSuccess}
            raffle={raffle}
          />
        )}
      </CardBody>
    </Card>
  );
};

interface FormProps {
  readonly raffle: Raffle;
  readonly onSuccess: (results: RunRaffleResults) => void;
  readonly onFailure: (err: Error) => boolean;
  readonly onSubmit: () => void;
}

const DrawRaffleModalForm: FC<FormProps> = ({ raffle, onFailure, onSubmit, onSuccess }) => {
  const { ts } = useTranslation();
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  // TODO: Figure out if `user` is reachable.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const [runType, setRunType] = useState<string>(raffle.criterion ? 'criterion' : 'user');

  const preSubmit = useCallback(
    ({ inputs }: PreSubmitData<{ customRecipients: number[] }>) => {
      if (runType === 'user') {
        inputs.customRecipients = selectedUsers;
      }

      onSubmit();
    },
    [onSubmit, runType, selectedUsers],
  );

  return (
    <JSONForm<RunRaffleResults, { customRecipients: number[] }>
      method="post"
      onFailure={onFailure}
      onSuccess={onSuccess}
      path={`/api/raffles/${raffle.id}/run`}
      preSubmit={preSubmit}
    >
      <ModalBody>
        <FormGroup>
          <Label>{ts('run_type')}</Label>
          <RadioButtonGroup
            defaultSelected={runType}
            id="runType"
            onChange={setRunType}
            options={[
              {
                label: 'common.criterion',
                value: 'criterion',
                icon: 'public',
                disabled: !raffle.criterion,
              },
              { label: 'common.user_selection', value: 'user', icon: 'link' },
            ]}
          />
        </FormGroup>
        {runType === 'criterion' && (
          <FormGroup>
            <Label>{ts('runraffle.number_of_winners_to_draw')}</Label>
            <Input id="maxWinners" min={1} name="maxWinners" required step={1} type="number" />
          </FormGroup>
        )}
        {runType === 'user' && (
          <FormGroup>
            <Label>{ts('runraffle.select_users_to_win')}</Label>
            <UserSelector id="customRecipients" selectionIdsChanged={setSelectedUsers} />
          </FormGroup>
        )}

        <FormGroup>
          <Label>
            {ts('what_should_happen_to_existing_winners')}
            <InlineHelp>
              <RichMarkdownIt source={ts('runraffle.help_text')} />
            </InlineHelp>
          </Label>
          <Input id="currentWinnersAction" name="currentWinnersAction" type="select">
            <option value="nothing">{ts('runraffle.do_nothing')}</option>
            <option value="revoke">{ts('runraffle.revoke_existing_wins')}</option>
            <option value="reset">{ts('runraffle.reset_existing_wins')}</option>
            <option value="delete">{ts('runraffle.delete_existing_wins')}</option>
          </Input>
        </FormGroup>
        <FormGroup>
          <Label>
            {ts('what_should_happen_to_non_winners_in_this_draw')}
            <InlineHelp>
              <RichMarkdownIt source={ts('runraffle.help_text_after')} />
            </InlineHelp>
          </Label>
          <Input id="nonWinnerAction" name="nonWinnerAction" type="select">
            <option value="nothing">{ts('runraffle.do_nothing')}</option>
            <option value="revoke">{ts('runraffle.revoke_tickets')}</option>
            <option value="delete">{ts('runraffle.delete_tickets')}</option>
          </Input>
        </FormGroup>
        <FormGroup check className="mt-3">
          <Input defaultChecked id="closeRaffle" name="closeRaffle" type="checkbox" />
          <Label check for="closeRaffle">
            {ts('runraffle.close_raffle')}
          </Label>
        </FormGroup>
        <FormGroup check className="mt-3">
          <Input id="givePointsToLosers" name="givePointsToLosers" type="checkbox" />
          <Label check for="givePointsToLosers">
            {ts('runraffle.give_non_winners_a_ticket_point')}
          </Label>
        </FormGroup>
        <FormGroup check className="mt-3">
          <Input id="testRun" name="testRun" type="checkbox" />
          <Label check for="testRun">
            {ts('runraffle.perform_a_test_run_dont_save')}
          </Label>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" id="drawRaffleBtn" type="submit">
          {ts('runraffle.run_raffle_draw')}
        </Button>
      </ModalFooter>
    </JSONForm>
  );
};

interface ResultProps {
  readonly results: RunRaffleResults;
  readonly onClick: () => void;
}

const RaffleResultsViewer: FC<ResultProps> = ({ onClick, results }) => {
  const { ts } = useTranslation();
  const entryCount = results.weights.length;
  const winnerCount = results.winners.length;

  if (entryCount === 0) {
    return (
      <>
        <ModalBody className="text-center" id="raffleResultsError">
          <div>
            <MaterialIcon large name="whatshot" type="danger" />
            <h4>{ts('raffle_draw_compl_wait_what')}</h4>
          </div>
          <hr />
          <p>{ts('raffle_draw_compl_wait_what_explanation')}</p>
          <p>
            <small>{ts('raffle_draw_compl_wait_what_explanation_small')}</small>
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onClick} outline>
            {ts('common.close')}
          </Button>
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      <ModalBody className="text-center" id="raffleResults">
        <div>
          <MaterialIcon large name="check_circle" type="success" />
          <h4>{ts(results.testRun ? 'test_run_draw_results' : 'raffle_draw_complete')}</h4>
        </div>
        <hr />
        <Row>
          <Col className="mb-3" id="raffleTotalWinners" lg={6} xs={12}>
            <h4>{ts('number_of_winners')}</h4>
            <h5>{winnerCount}</h5>
          </Col>
          <Col className="mb-3" id="raffleTotalEntrants" lg={6} xs={12}>
            <h4>{ts('total_entries')}</h4>
            <h5>{entryCount}</h5>
          </Col>
          <Col id="raffleWinPercentage" xs={12}>
            <h4>{ts('win_percentage')}</h4>
            <h5>{((winnerCount / entryCount) * 100).toFixed(2)} %</h5>
          </Col>
          <Col xs={12}>
            <hr />
          </Col>
          <Col className="text-start" id="raffleWinnerIds" xs={12}>
            <h4>{ts('winning_user_ids')}</h4>
            <p>{results.winners.join(', ')}</p>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClick} outline>
          {ts('common.close')}
        </Button>
      </ModalFooter>
    </>
  );
};
